import React from 'react';
import { Route, Switch } from 'react-router-dom';
import { useFeature } from 'flagged';

import { FeatureFlags } from '../common/constants';
import asyncComponent from '../components/AsyncComponent';
import AuthenticatedRoute from '../components/AuthenticatedRoute';
import UnauthenticatedRoute from '../components/UnauthenticatedRoute';
import { ROLES } from '../libs/Constants';
import RoleCheck from '../libs/RoleCheck';
import { PATHS } from './routes.constants';

const AsyncLogin = asyncComponent(() =>
  import('../containers/Login/indexDragonfly')
);
const AsyncSSO = asyncComponent(() => import('../containers/SSO'));
const AsyncQueue = asyncComponent(() => import('../containers/Queue'));
const AsyncProfile = asyncComponent(() => import('../containers/Profile'));
const AsyncDashboard = asyncComponent(() => import('../containers/Dashboard'));
const AsyncReporting = asyncComponent(() => import('../containers/Reporting'));
const AsyncNotFound = asyncComponent(() => import('../containers/NotFound'));
const AsyncUsers = asyncComponent(() => import('../containers/Admin'));
const AsyncNewPassword = asyncComponent(() =>
  import('../containers/NewPassword')
);
const AsyncReviews = asyncComponent(() => import('../containers/Reviews'));
const AsyncTeam = asyncComponent(() => import('../containers/Team'));
const AsyncUser = asyncComponent(() => import('../containers/User'));
const AsyncFeature = asyncComponent(() => import('../containers/Feature'));
const AsyncSearch = asyncComponent(() => import('../containers/Search'));
const AsyncEntityRule = asyncComponent(() =>
  import('../containers/EntityRule')
);
const AsyncEntityContacts = asyncComponent(() =>
  import('../containers/EntityContacts')
);
const AsyncCMS = asyncComponent(() => import('../containers/CMS'));
const AsyncChart = asyncComponent(() => import('../containers/Chart'));
const AsyncChartRR = asyncComponent(() =>
  import('../containers/Chart/indexRR')
);
const AsyncViewAndDo = asyncComponent(() => import('../containers/ViewAndDo'));
const AsyncRecentlyViwedPatients = asyncComponent(() =>
  import('../containers/RecentlyViewedQueue')
);
const AsyncMessageCenter = asyncComponent(() =>
  import('../containers/MessageCenter')
);

const PatientWorkQueueAgGrid = asyncComponent(() =>
  import('../features/PatientWorkQueue/PatientWorkQueue.connected')
);

const Routes = ({ childProps }) => {
  const isPatientWorkQueueAgGridEnabled = useFeature(FeatureFlags.PWQ_AG_GRID);
  const PatientWorkQueue = isPatientWorkQueueAgGridEnabled
    ? PatientWorkQueueAgGrid
    : AsyncQueue;

  return (
    <Switch>
      <AuthenticatedRoute
        path={PATHS.HOME}
        exact
        component={PatientWorkQueue}
        props={childProps}
      />
      <UnauthenticatedRoute
        path={PATHS.LOGIN}
        exact
        component={AsyncLogin}
        props={childProps}
      />
      <UnauthenticatedRoute
        path={PATHS.SSO}
        exact
        component={AsyncSSO}
        props={childProps}
      />
      <UnauthenticatedRoute
        path={PATHS.NEW_PASSWORD}
        exact
        component={AsyncNewPassword}
        props={childProps}
      />
      <AuthenticatedRoute
        path={PATHS.PROFILE}
        exact
        component={AsyncProfile}
        props={childProps}
      />
      <AuthenticatedRoute
        path={PATHS.QUEUE}
        exact
        component={PatientWorkQueue}
        props={childProps}
      />
      <AuthenticatedRoute
        path={PATHS.TEAMS_QUEUE}
        exact
        component={PatientWorkQueue}
        props={childProps}
      />
      <AuthenticatedRoute
        path={PATHS.DASHBOARD}
        exact
        component={AsyncDashboard}
        props={childProps}
      />
      <AuthenticatedRoute
        path={PATHS.RECENTLY_VIEWED_PATIENTS}
        exact
        component={AsyncRecentlyViwedPatients}
        props={childProps}
      />

      {RoleCheck(childProps.userRoles, [
        ROLES.SYSTEM_ADMINISTRATOR,
        ROLES.ADMINISTRATOR,
        ROLES.TEAM_LEAD,
        ROLES.SUPPORT,
      ]) && (
        <AuthenticatedRoute
          path={PATHS.REPORTING}
          exact
          component={AsyncReporting}
          props={childProps}
        />
      )}

      {RoleCheck(childProps.userRoles, [
        ROLES.SYSTEM_ADMINISTRATOR,
        ROLES.ADMINISTRATOR,
      ]) && (
        <AuthenticatedRoute
          path={PATHS.ADMIN}
          exact
          component={AsyncUsers}
          props={childProps}
        />
      )}

      {RoleCheck(childProps.userRoles, [
        ROLES.SYSTEM_ADMINISTRATOR,
        ROLES.ADMINISTRATOR,
      ]) && (
        <AuthenticatedRoute
          path={PATHS.ENTITY_RULES}
          exact
          component={AsyncEntityRule}
          props={childProps}
        />
      )}

      {RoleCheck(childProps.userRoles, [
        ROLES.SYSTEM_ADMINISTRATOR,
        ROLES.ADMINISTRATOR,
      ]) && (
        <AuthenticatedRoute
          path={PATHS.ENTITY_CONTACTS}
          exact
          component={AsyncEntityContacts}
          props={childProps}
        />
      )}

      <AuthenticatedRoute
        path={PATHS.ADMISSION_REVIEWS}
        component={AsyncReviews}
        props={childProps}
      />
      <AuthenticatedRoute
        path={PATHS.ADMISSION_RR_REVIEWS}
        component={AsyncViewAndDo}
        props={childProps}
      />
      <AuthenticatedRoute
        path={PATHS.TEAM}
        exact
        component={AsyncTeam}
        props={childProps}
      />
      <AuthenticatedRoute
        path={PATHS.USER}
        exact
        component={AsyncUser}
        props={childProps}
      />
      <AuthenticatedRoute
        path={PATHS.USER_NAME}
        exact
        component={AsyncUser}
        props={childProps}
      />
      <AuthenticatedRoute
        path={PATHS.FEATURES}
        exact
        component={AsyncFeature}
        props={childProps}
      />
      <AuthenticatedRoute
        path={PATHS.FEATURE}
        exact
        component={AsyncFeature}
        props={childProps}
      />
      <AuthenticatedRoute
        path={PATHS.SEARCH}
        exact
        component={AsyncSearch}
        props={childProps}
      />
      <AuthenticatedRoute
        path={PATHS.CMS}
        exact
        component={AsyncCMS}
        props={childProps}
      />
      <AuthenticatedRoute
        path={PATHS.ENTITY_CHART}
        component={AsyncChart}
        props={childProps}
      />
      <AuthenticatedRoute
        path={PATHS.RR_ENTITY_CHART}
        component={AsyncChartRR}
        props={childProps}
      />
      <AuthenticatedRoute
        path={PATHS.REVIEW}
        component={AsyncChart}
        props={{ ...childProps, isReview: true }}
      />
      <AuthenticatedRoute
        path={PATHS.RR_REVIEW}
        component={AsyncViewAndDo}
        props={{ ...childProps, isReview: true }}
      />
      <AuthenticatedRoute
        path={PATHS.VIEW_AND_DO}
        component={AsyncViewAndDo}
        props={childProps}
      />
      <AuthenticatedRoute
        path={PATHS.MESSAGE_CENTER}
        component={AsyncMessageCenter}
        props={childProps}
      />
      {/* Finally, catch all unmatched routes */}
      <Route component={AsyncNotFound} />
    </Switch>
  );
};

export default Routes;
